import { Divider, message } from "antd";
import _ from "lodash";
import { format } from "date-fns";
import { useState } from "react";
import {
  consultationColorCoding,
  copyToClipboard,
  generateFileUrl,
  hasPermission,
  isImageFile,
  isPdfFile,
} from "../../../helpers/utilities";
import { Badge, Paper, Menu, Loader, Modal } from "@mantine/core";
import {
  DetailLineItem,
  FeedBackView,
  RatingDisplay,
} from "../../../components";
import {
  IconAlarm,
  IconCheck,
  IconForms,
  IconMail,
  IconClock,
  IconMessageCircle,
  IconPill,
  IconRoad,
  IconStethoscope,
  IconTrash,
  IconHistory,
  IconNews,
  IconLockOpen,
  IconUpload,
  IconReportMedical,
} from "@tabler/icons";
import smalltalk from "smalltalk";
import {
  deleteConsultation,
  deleteConsultationFile,
  getConsultationById,
  getMoremiDecisionSupport,
  getSendReminder,
  putUpdateConsultationStatus,
} from "../../../helpers/api";
import EditConsultationComponent from "./edit-consultation";
import { EditPatientForm } from "../../catalog/components/edit-patient";
import EditDiagnosisComponent from "./edit-diagnosis";
import EditReviewComponent from "./edit-review";
import EditPrescriptionComponent from "./edit-prescription";
import EditLaboratoryComponent from "./edit-laboratory";
import EditPharmacyComponent from "./edit-pharmacy";
import ExcuseDutyForm from "../../../components/shared/excuse-duty";
import PatientJourney from "../../../components/shared/patient-journey";
import MedicalHistory from "../../../components/shared/medical-history";
import MedicalReports from "../../../components/shared/medical-reports";
import { useQuery } from "react-query";
import { useEffect } from "react";
import { action, resouces } from "../../../helpers/auth";
import pdfIcon from "../../../static/icons/pdf.png";
import EditVitals from "./edit-vitals";
import ViewVitals from "./view-vitals";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import PhotoSwipe from "photoswipe";
import "photoswipe/style.css";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import UploadFiles from "./upload-files";
import { CONSULTATION_ATTACHMENT_LIMIT } from "../../../helpers/config";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
const MAX_ATTACHMENTS = CONSULTATION_ATTACHMENT_LIMIT;

const ConsultationDetail = ({ consultationId, refresh }) => {
  const {
    data = null,
    isFetching,
    refetch,
  } = useQuery({
    queryFn: () => getConsultationById(consultationId),
    queryKey: ["consultation", consultationId],
    enabled: false,
  });

  useEffect(() => {
    if (consultationId) refetch();
  }, [consultationId]);

  // Initialize PhotoSwipe
  useEffect(() => {
    const imageFiles = data?.attachments?.filter((f) =>
      isImageFile(f.fileName)
    );

    if (imageFiles && imageFiles?.length > 0) {
      const lightboxInstance = new PhotoSwipeLightbox({
        gallery: ".pswp-gallery",
        children: "a",
        pswpModule: PhotoSwipe,
      });

      lightboxInstance.init();
      setLightbox(lightboxInstance);

      return () => {
        lightboxInstance.destroy();
      };
    }
  }, [data?.attachments]);

  const [modal, setModal] = useState({
    open: false,
    title: "",
    size: 700,
    child: null,
  });
  const [aiModal, setAiModal] = useState({
    open: false,
    loading: false,
    result: null,
    error: null,
  });

  // State for PhotoSwipe lightbox
  const [lightbox, setLightbox] = useState(null);

  // State for PDF viewer
  const [pdfModal, setPdfModal] = useState({
    open: false,
    url: "",
    fileName: "",
  });
  const [numPages, setNumPages] = useState(null);
  let totalPrice = 0;
  data?.prescriptions?.map(
    (d) => (totalPrice = parseFloat(totalPrice) + parseFloat(d.price))
  );

  const openModal = (title, child, size = 700) => {
    setModal({
      title,
      child,
      size,
      open: true,
    });
  };

  const fetchDecisionSupport = async () => {
    setAiModal((m) => ({ ...m, open: true, loading: true, result: null, error: null }));
    try {
      const memberId = data?.patient?.id || data?.patientId;
      if (!memberId) {
        setAiModal((m) => ({
          ...m,
          loading: false,
          result: null,
          error: "Member ID not found.",
        }));
        return;
      }

      const res = await getMoremiDecisionSupport({ memberId });
      setAiModal((m) => ({ ...m, loading: false, result: res, error: null }));
    } catch (err) {
      if (err.message?.includes("API key")) message.error(err.message);
      const msg = err.response?.data?.message || err.message || "Request failed.";
      setAiModal((m) => ({ ...m, loading: false, result: null, error: msg }));
    }
  };

  const recommendationItems = aiModal?.result?.recommendation
    ? aiModal.result.recommendation
        .split(/\s(?=\d+\.\s)/)
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

  // Handle PDF click to open PDF viewer
  const handlePdfClick = (file) => {
    setPdfModal({
      open: true,
      url: file.fileUrl || generateFileUrl(file.fileName),
      fileName: file.fileName,
    });
    setNumPages(null); // Reset page count
  };

  // Handle file deletion
  const handleDeleteFile = async (file) => {
    try {
      await smalltalk
        .confirm(
          "Delete File",
          `Are you sure you want to delete "${file.fileName}"? This action cannot be undone.`,
          {
            buttons: {
              ok: "YES",
              cancel: "NO",
            },
            zIndex: 9999,
          }
        )
        .then(async (go) => {
          if (go) message.info("deleting...");

          const response = await deleteConsultationFile(
            consultationId,
            file.id
          );
          if (response.status === 200) {
            message.success("File deleted successfully!");
            refetch(); // Refresh the consultation data
          } else {
            message.error("Failed to delete file");
          }
        });
    } catch (error) {
      if (error.message !== "User cancelled") {
        message.error("Failed to delete file");
      }
    }
  };

  // Render file thumbnail
  const renderFileThumbnail = (file, index) => {
    const fName = file.fileName;
    const isImage = isImageFile(fName);
    const isPdf = isPdfFile(fName);

    return (
      <div key={index} className="col-md-2 col-sm-3 col-4" title={fName}>
        <div
          className="border rounded p-2 text-center position-relative"
          style={{ height: "120px", cursor: "pointer" }}>
          {/* Delete button */}

          {!data?.isComplete && (
            <button
              className="btn btn-sm btn-outline-danger position-absolute"
              style={{
                top: "2px",
                right: "2px",
                zIndex: 10,
                padding: "2px 4px",
                fontSize: "0.7rem",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFile(file);
              }}
              title="Delete file">
              <i className="bi bi-trash" style={{ fontSize: "0.6rem" }}></i>
            </button>
          )}

          {isPdf ? (
            <div onClick={() => handlePdfClick(file)}>
              <img
                src={pdfIcon}
                alt="PDF"
                style={{
                  height: "60px",
                  width: "60px",
                  objectFit: "contain",
                }}
              />
              <p
                className="text-muted small mt-2 mb-0"
                style={{ fontSize: "0.75rem" }}>
                {fName.length > 10 ? fName.substring(0, 10) + "..." : fName}
              </p>
            </div>
          ) : isImage ? (
            <a
              href={file.fileUrl || generateFileUrl(fName)}
              data-pswp-width="1000"
              data-pswp-height="800"
              // target="_blank"
              style={{ textDecoration: "none", color: "inherit" }}>
              <img
                src={file.fileUrl || generateFileUrl(fName)}
                alt={fName}
                style={{
                  height: "100px",
                  // maxWidth: "100%",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
                crossOrigin="anonymous"
              />
              <p
                className="text-muted small mt-2 mb-0"
                style={{ fontSize: "0.75rem" }}>
                {fName.length > 10 ? fName.substring(0, 10) + "..." : fName}
              </p>
            </a>
          ) : (
            <div>
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ height: "60px" }}>
                <i
                  className="bi bi-file-earmark"
                  style={{ fontSize: "2rem", color: "#6c757d" }}></i>
              </div>
              <p
                className="text-muted small mt-2 mb-0"
                style={{ fontSize: "0.75rem" }}>
                {fName.length > 15 ? fName.substring(0, 15) + "..." : fName}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const preview = (file, index) => {
    const fName = file.fileName;
    const ext = fName.slice(fName.indexOf("."));

    return (
      <div className="col-4 border" title="lab report attachment">
        {ext === ".pdf" ? (
          // use a tag and etc etc
          <a href={generateFileUrl(fName)} target="_blank">
            <img
              key={index}
              style={{
                height: "100px",
                width: "100px",
              }}
              src={pdfIcon}
            />
          </a>
        ) : (
          <a href={generateFileUrl(fName)} className="d-block" target="_blank">
            <img
              key={index}
              style={{
                height: "100px",
                width: "130px",
              }}
              crossOrigin="anonymous" //simple trick to get crossorigin error off
              src={generateFileUrl(fName)}
              alt="laboratory report image"
            />
          </a>
        )}
      </div>
    );
  };

  const handleMarkComplete = () => {
    smalltalk
      .confirm(
        "Update Status",
        "You are about to update the status of this consultation. Continue?",
        {
          buttons: {
            ok: "YES",
            cancel: "NO",
          },
        }
      )
      .then((go) => {
        message.info("please wait...");
        putUpdateConsultationStatus(data.id)
          .then((res) => {
            if (res.status === 200) {
              message.success(res.data.message);
              refresh();
              refetch();
              return;
            }
          })
          .catch((ex) => {
            message.error(ex.data.message);
          });
      })
      .catch((ex) => {
        return false;
      });
  };
  const handleDelete = () => {
    smalltalk
      .confirm(
        "Delete Consultation",
        "Deleting this consultation cannot be undone. Continue?",
        {
          buttons: {
            ok: "YES",
            cancel: "NO",
          },
        }
      )
      .then((go) => {
        message.info("deleting...");
        deleteConsultation(data.id)
          .then((res) => {
            if (res.status === 204) {
              message.success(res.data.message);
              // reload();
              refresh();
              refetch();
              return;
            }
          })
          .catch((ex) => {
            message.error(ex.data.message);
          });
      })
      .catch((ex) => {
        return false;
      });
  };

  // updates
  // const handleGenerateDutyForm = () => { };
  // const handleSendSMS = () => { };
  // const handleSendEmail = () => { };

  const handleSendReminder = () => {
    getSendReminder(data.id).then((res) => {
      if (res.status === 200) {
        message.success(res.data.message);
      }
    });
  };

  return (
    <>
      {isFetching && (
        <>
          <Loader className="me-3" />
          <span>Please wait...</span>
        </>
      )}
      {_.isEmpty(data) && !isFetching && (
        <p className="p-3"> select a consultation to view details</p>
      )}
      {!_.isEmpty(data) && (
        <div className="consultation-detail">
          <div className="row g-0">
            <div className="col-12 mb-2">
              <h6 className="section-header mb-0 d-flex">Consultation</h6>
              <p className="text-muted mt-0">
                basic details of selected consultation
              </p>
              {/* consultation */}
              <Paper className="p-3  border">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="layer-header d-flex">
                    Consultation
                    {data?.feedback?.rating && (
                      <span className="d-flex ms-2">
                        {" "}
                        - <RatingDisplay rate={data?.feedback?.rating} />
                        <span className="ms-1 bi bi-star-fill text-warning" />
                      </span>
                    )}
                  </h6>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="app-btn btn-text"
                      onClick={fetchDecisionSupport}
                      disabled={!data?.patient?.id}>
                      <span className="bi bi-robot me-2" />
                      AI Decision Support
                    </button>
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <button className="app-btn btn-text">
                          <span className="bi bi-grid me-2"></span>
                          Action
                        </button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {(hasPermission(
                          resouces.consultations,
                          action.canUpdate
                        ) ||
                          hasPermission(
                            resouces.consultations,
                            action.canDelete
                          )) && <Menu.Label>Consultation</Menu.Label>}
                        {!data.isComplete &&
                          hasPermission(
                            resouces.consultations,
                            action.canUpdate
                          ) ? (
                          <Menu.Item
                            color="green"
                            icon={<IconCheck size={14} />}
                            onClick={handleMarkComplete}>
                            Mark Complete
                          </Menu.Item>
                        ) : (
                          <Menu.Item
                            color="black"
                            icon={<IconLockOpen size={14} />}
                            onClick={handleMarkComplete}>
                            Re-Open
                          </Menu.Item>
                        )}
                        {hasPermission(
                          resouces.consultations,
                          action.canDelete
                        ) && (
                            <Menu.Item
                              color="red"
                              icon={<IconTrash size={14} />}
                              onClick={handleDelete}>
                              Delete
                            </Menu.Item>
                          )}
                        {hasPermission(
                          resouces.consultations,
                          action.canUpdate
                        ) && (
                            <Menu.Item
                              icon={<IconUpload size={14} />}
                              disabled={
                                data?.attachments?.length >= MAX_ATTACHMENTS
                              }
                              onClick={() =>
                                openModal(
                                  "Upload Files",
                                  <UploadFiles
                                    consultationId={data.id}
                                    onUpload={(files) => {
                                      message.success(
                                        "Files uploaded successfully!"
                                      );
                                      refetch();
                                    }}
                                    onClose={() =>
                                      setModal({ ...modal, open: false })
                                    }
                                  />,
                                  600
                                )
                              }>
                              Upload Files
                            </Menu.Item>
                          )}
                        {(hasPermission(
                          resouces.consultations,
                          action.canUpdate
                        ) ||
                          hasPermission(
                            resouces.consultations,
                            action.canDelete
                          )) && <Menu.Divider />}
                        <Menu.Label>Patient</Menu.Label>
                        {hasPermission(resouces.patients, action.canCreate) && (
                          <Menu.Item
                            icon={<IconForms size={14} />}
                            onClick={() =>
                              openModal(
                                "Generate Excuse Duty",
                                <ExcuseDutyForm code={data.code} />,
                                500
                              )
                            }>
                            Excuse Duty
                          </Menu.Item>
                        )}
                        <Menu.Item
                          icon={<IconRoad size={14} />}
                          onClick={() =>
                            openModal(
                              "Patient Journey",
                              <PatientJourney patientId={data.patient.id} />,
                              1200
                            )
                          }>
                          Patient Journey
                        </Menu.Item>
                        <Menu.Item
                          icon={<IconHistory size={14} />}
                          onClick={() =>
                            openModal(
                              "Medical History",
                              <MedicalHistory
                                patientId={data.patient.id}
                                isEditable={hasPermission(
                                  resouces.patients,
                                  action.canUpdate
                                )}
                              />
                            )
                          }>
                          Medical History
                        </Menu.Item>
                        {hasPermission(
                          resouces.consultations,
                          action.canRead
                        ) &&
                          data?.patient?.id && (
                            <Menu.Item
                              icon={<IconReportMedical size={14} />}
                              onClick={() =>
                                openModal(
                                  "Generate Medical Report",
                                  <MedicalReports
                                    patientId={data.patient.id}
                                    patientName={
                                      [data.patient.firstName, data.patient.lastName]
                                        .filter(Boolean)
                                        .join(" ")
                                        .trim() || undefined
                                    }
                                    initialConsultationIdForGeneration={data.id}
                                    onUpdated={refetch}
                                  />,
                                  1200
                                )
                              }>
                              Generate Medical Report
                            </Menu.Item>
                          )}
                        <Menu.Item
                          disabled={_.isEmpty(data.feedback)}
                          onClick={() =>
                            openModal(
                              "Feedback",
                              <FeedBackView consultationId={data.id} />
                            )
                          }
                          icon={<IconNews size={14} />}>
                          Feedback
                        </Menu.Item>
                        {!data.isComplete &&
                          hasPermission(
                            resouces.consultations,
                            action.canUpdate
                          ) && (
                            <>
                              <Menu.Divider />
                              <Menu.Label>Messaging</Menu.Label>
                              <Menu.Item
                                icon={<IconAlarm size={14} />}
                                onClick={handleSendReminder}>
                                Send Reminder
                              </Menu.Item>
                            </>
                          )}
                      </Menu.Dropdown>
                    </Menu>
                    {hasPermission(
                      resouces.consultations,
                      action.canUpdate
                    ) && (
                        <button
                          className="app-btn btn-text"
                          onClick={() =>
                            openModal(
                              "Edit Consultation",
                              <EditConsultationComponent
                                consultationId={data.id}
                                onRefresh={refetch}
                              />,
                              900
                            )
                          }>
                          <span className="bi bi-pencil me-2"></span>
                          Update
                        </button>
                      )}
                  </div>
                </div>
                <DetailLineItem
                  icon="lock"
                  title="Code"
                  detail={
                    <>
                      <Badge
                        color={consultationColorCoding(
                          data.completedTasks,
                          data.totalTasks
                        )}>
                        {data.code}
                      </Badge>
                      <button
                        className="app-btn btn-text p-1 ms-2"
                        onClick={() => {
                          copyToClipboard(data.code).then((done) =>
                            message.success("code copied!")
                          );
                        }}>
                        copy
                      </button>
                    </>
                  }
                />
                <Divider className="my-1" />
                <DetailLineItem
                  icon="file-font"
                  title="Folder Number"
                  detail={data?.folderNumber || "-"}
                />
                <Divider className="my-1" />
                <DetailLineItem
                  icon="braces-asterisk"
                  title="Type"
                  detail={data.type}
                />
                <Divider className="my-1" />
                <DetailLineItem
                  icon="calendar"
                  title="Date"
                  detail={`${format(
                    new Date(data.createdAt),
                    "EEE MMM dd, yyy"
                  )} @${new Date(data.createdAt).toLocaleTimeString()}`}
                />
                <Divider className="my-1" />
                <DetailLineItem
                  icon="list-task"
                  title="Tasks"
                  detail={`Total: ${data.totalTasks} - Complete: ${data.completedTasks}`}
                />
                {/* detail={ `Total: ${ data.totalTasks } - Complete: ${ data.completedTasks } ---> (${ percentage( data?.completedTasks, data?.totalTasks ) }% complete)` } /> */}
                <Divider className="my-1" />
                <DetailLineItem
                  icon="megaphone"
                  title="Complaint"
                  detail={
                    data.rawComplaint ? (
                      <div
                        className="consultation-html"
                        dangerouslySetInnerHTML={{ __html: data.rawComplaint }}
                      />
                    ) : (
                      "-"
                    )
                  }
                />
                <Divider className="my-1" />
                <DetailLineItem
                  icon="card-text"
                  title="Notes"
                  detail={
                    data.rawNotes ? (
                      <div
                        className="consultation-html"
                        dangerouslySetInnerHTML={{ __html: data.rawNotes }}
                      />
                    ) : (
                      "-"
                    )
                  }
                />
                <Divider className="my-1" />
                <DetailLineItem
                  icon="incognito"
                  title="Investigation"
                  detail={
                    data.rawInvestigation ? (
                      <div
                        className="consultation-html"
                        dangerouslySetInnerHTML={{
                          __html: data.rawInvestigation,
                        }}
                      />
                    ) : (
                      "-"
                    )
                  }
                />
                <Divider className="my-1" />
                <DetailLineItem
                  icon="file-medical"
                  title="Management Plan"
                  detail={
                    data.rawDoctorsAdvise ? (
                      <div
                        className="consultation-html"
                        dangerouslySetInnerHTML={{
                          __html: data.rawDoctorsAdvise,
                        }}
                      />
                    ) : (
                      "-"
                    )
                  }
                />
              </Paper>
            </div>
            <div className="col-md-6 g-0 col-12">
              {/* patient */}
              <Paper className="p-3  border">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="layer-header">
                    <IconPill className="me-1" />
                    Patient
                  </h6>
                  <button
                    className="app-btn btn-text m-0"
                    onClick={() =>
                      openModal(
                        "View Patient",
                        <EditPatientForm
                          patientId={data.patient?.id}
                          showCharm={false}
                          isEditable={hasPermission(
                            resouces.patients,
                            action.canUpdate
                          )}
                        />
                      )
                    }>
                    view
                  </button>
                </div>
                <DetailLineItem
                  title="Name"
                  detail={`${data?.patient?.firstName} ${data?.patient?.lastName}`}
                />
                {/* <DetailLineItem title="Gender" detail={ data.patient.gender } /> */}
                <DetailLineItem
                  title="Contact"
                  detail={data.patient?.contact}
                />
                {/* <DetailLineItem title="Insurance" detail={ data.patient?.insuranceNumber } /> */}
              </Paper>
            </div>
            <div className="col-md-6 g-0 col-12">
              {/* doctor */}
              <Paper className="p-3 border">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="layer-header">
                    <IconStethoscope className="me-1" />
                    Doctor
                  </h6>
                  {/* <button className="app-btn btn-text">
                    view
                  </button> */}
                </div>
                <DetailLineItem
                  title="Name"
                  detail={`${data?.doctor?.firstName || "-"} ${data?.doctor?.lastName || ""
                    }`}
                />
                <DetailLineItem
                  title="Contact"
                  detail={data?.doctor?.contact}
                />
              </Paper>
            </div>
          </div>
          <div className="row my-4">
            <div className="col-12 mb-1">
              <h6 className="section-header mb-0">Results</h6>
              <p className="text-muted mt-0">
                findings and recommendations for further engagement
              </p>
              {/* files */}
              <div className="mb-1">
                <Paper className="p-3 border">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="layer-title text-success">
                      Attachments
                    </span>
                  </div>
                  <div className="row mt-2 pswp-gallery pb-3">
                    {data?.attachments?.length > 0 ? (
                      data?.attachments
                        .slice(0, MAX_ATTACHMENTS)
                        .map((file, index) => renderFileThumbnail(file, index))
                    ) : (
                      <div className="col-12">
                        <p className="text-muted text-center">
                          No files attached
                        </p>
                      </div>
                    )}
                  </div>
                  {/* <div className="text-center mt-2">
                    <small className="text-muted">
                      Showing {data?.attachments?.length} of {MAX_ATTACHMENTS}{" "}
                      files
                    </small>
                  </div> */}
                </Paper>
              </div>
              <div className="mb-1">
                <Paper className="p-3 border">
                  {/* vitals */}
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="layer-title text-success">Vitals</span>

                    {!data.isComplete &&
                      hasPermission(
                        resouces.consultations,
                        action.canUpdate
                      ) && (
                        <button
                          className="app-btn btn-text"
                          onClick={() =>
                            openModal(
                              "Patient Vitals",
                              <EditVitals
                                consultationId={data.id}
                                isComplete={data.isComplete}
                                onRefresh={refetch}
                              />
                            )
                          }>
                          <span className="bi bi-pencil me-2"></span>
                          Edit
                        </button>
                      )}
                  </div>
                  <div>
                    {data?.vitals && <ViewVitals vitals={data.vitals} />}
                    {data?.initiator && (
                      <div className="row mt-2">
                        <div className="col-12">
                          <strong>Captured By</strong>:{" "}
                          {`${data?.initiator?.firstName} ${data?.initiator?.lastName
                            } (${data?.initiator?.contact || "-"})`}
                        </div>
                      </div>
                    )}
                  </div>
                </Paper>
              </div>
              <Paper className="p-3 border">
                {/* diagnosis */}
                <div className="d-flex justify-content-between align-items-center">
                  <span className="layer-title text-success">Diagnosis</span>
                  {!data.isComplete &&
                    hasPermission(resouces.consultations, action.canUpdate) && (
                      <button
                        className="app-btn btn-text"
                        onClick={() =>
                          openModal(
                            "Diagnosis",
                            <EditDiagnosisComponent
                              consultationId={data.id}
                              onRefresh={refetch}
                            />
                          )
                        }>
                        <span className="bi bi-pencil me-2"></span>
                        Edit
                      </button>
                    )}
                </div>
                <div>
                  {data.diagnosis.map((d, i) => (
                    <div key={i}>
                      <Divider className="my-1" />
                      <DetailLineItem
                        firstCol="col-2"
                        secondCol="col-10"
                        title={++i + "."}
                        detail={d.diagnosis}
                      />
                    </div>
                  ))}
                </div>
              </Paper>
            </div>

            <div className="col-12 mb-1">
              <Paper className="p-3 border">
                {/* reviews */}
                <div className="d-flex justify-content-between align-items-center">
                  <span className="layer-title text-success">
                    Systemic Reviews
                  </span>

                  {!data.isComplete &&
                    hasPermission(resouces.consultations, action.canUpdate) && (
                      <button
                        className="app-btn btn-text"
                        onClick={() =>
                          openModal(
                            "Systemic Reviews",
                            <EditReviewComponent
                              consultationId={data.id}
                              onRefresh={refetch}
                            />
                          )
                        }>
                        <span className="bi bi-pencil me-2"></span>
                        Edit
                      </button>
                    )}
                </div>
                <div>
                  {data.reviews.map((r, i) => (
                    <div key={i}>
                      <Divider className="my-1" />
                      <DetailLineItem title="Type" detail={r.type} />
                      <DetailLineItem title="Symptom" detail={r.symptom} />
                    </div>
                  ))}
                </div>
              </Paper>
            </div>
            <div className="col-12 mb-1">
              <Paper className="p-3 border">
                {/* prescriptions */}
                <div className="d-flex justify-content-between align-items-center">
                  <span className="layer-title text-success">
                    Prescriptions
                    {totalPrice > 0 && (
                      <kbd className="ms-2">GHS {totalPrice}</kbd>
                    )}
                  </span>

                  {!data.isComplete &&
                    hasPermission(resouces.consultations, action.canUpdate) && (
                      <button
                        className="app-btn btn-text"
                        onClick={() =>
                          openModal(
                            "Prescriptions",
                            <EditPrescriptionComponent
                              consultationId={data.id}
                              onRefresh={refetch}
                            />
                          )
                        }>
                        <span className="bi bi-pencil me-2"></span>
                        Edit
                      </button>
                    )}
                </div>
                <div>
                  {data.prescriptions.map((d, i) => (
                    <div key={i}>
                      <Divider className="my-1" />
                      <kbd
                        className={d.isIssued ? "bg-success" : "bg-secondary"}>
                        {++i}
                      </kbd>
                      <DetailLineItem title="Medicine" detail={d.medicine} />
                      <DetailLineItem title="Strength" detail={d.strength} />
                      {/* <DetailLineItem title='Dosage' detail={ d.dosage } /> */}
                      <DetailLineItem title="Frequency" detail={d.frequency} />
                      <DetailLineItem title="Duration" detail={d.duration} />
                      {d.isIssued && (
                        <DetailLineItem
                          title="Price (GHS)"
                          detail={parseFloat(d.price).toFixed(2)}
                        />
                      )}
                      <DetailLineItem
                        title="Is Issued"
                        detail={
                          d.isIssued
                            ? `YES: By ${d.pharmacy?.pharmacyName} - (${format(
                              new Date(d.updatedAt),
                              "MMM dd, yy"
                            )} @ ${new Date(
                              d.updatedAt
                            ).toLocaleTimeString()})`
                            : "NO"
                        }
                      />
                    </div>
                  ))}
                </div>
              </Paper>
            </div>
            <div className="col-12 mb-1">
              <Paper className="p-3 border">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="layer-title text-success">Pharmacies</span>

                  {!data.isComplete &&
                    hasPermission(resouces.consultations, action.canUpdate) && (
                      <button
                        className="app-btn btn-text"
                        onClick={() =>
                          openModal(
                            "Pharmacies",
                            <EditPharmacyComponent
                              consultationId={data.id}
                              onRefresh={refetch}
                            />
                          )
                        }>
                        <span className="bi bi-pencil me-2"></span>
                        Edit
                      </button>
                    )}
                </div>
                <div>
                  {data.pharmacies.map((d, i) => (
                    <div key={i}>
                      <Divider className="my-1" />
                      <DetailLineItem
                        title={++i + "."}
                        detail={`${d.pharmacy.pharmacyName} - (${d.pharmacy.contact})`}
                      />
                      {d.notes && (
                        <>
                          <DetailLineItem
                            title={<kbd>Notes: </kbd>}
                            detail={<i>{d.notes}</i>}
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </Paper>
            </div>
            <div div className="col-12">
              <Paper className="p-3 border">
                {/* laboratories */}
                <div className="d-flex justify-content-between align-items-center">
                  <span className="layer-title text-success">Laboratories</span>

                  {!data.isComplete &&
                    hasPermission(resouces.consultations, action.canUpdate) && (
                      <button
                        className="app-btn btn-text"
                        onClick={() =>
                          openModal(
                            "Laboratories",
                            <EditLaboratoryComponent
                              consultationId={data.id}
                              onRefresh={refetch}
                            />
                          )
                        }>
                        <span className="bi bi-pencil me-2"></span>
                        Edit
                      </button>
                    )}
                </div>
                <div>
                  {data.laboratories.map((lab, i) => (
                    <div key={i}>
                      <Divider className="my-2" />
                      <DetailLineItem
                        title={<kbd>Lab: {++i} </kbd>}
                        detail={`${lab.laboratory.laboratoryName} - (${lab.laboratory.contact})`}
                      />
                      <DetailLineItem
                        title={<strong>Request: </strong>}
                        detail={lab.requestType}
                      />
                      {lab.bill > 0 && (
                        <>
                          <DetailLineItem
                            title={<strong>Bill: </strong>}
                            detail={"GHS " + lab.bill}
                          />
                        </>
                      )}
                      {lab.report && (
                        <>
                          <DetailLineItem
                            title={<strong>Report: </strong>}
                            detail={<i>{lab.report}</i>}
                          />
                        </>
                      )}
                      {
                        <div className="row mx-auto mt-3">
                          {data?.attachments?.length > 0 &&
                            data.attachments.map(
                              (file) =>
                                file.laboratoryId === lab.laboratoryId && (
                                  <>
                                    {/* <p className="mb-0">click to open attachments</p> */}
                                    {preview(file)}
                                  </>
                                )
                            )}
                        </div>
                      }
                    </div>
                  ))}
                </div>
              </Paper>
            </div>
          </div>
          {/* modals */}
          <Modal
            title={modal.title}
            size={modal.size}
            opened={modal.open}
            onClose={() => setModal({ ...modal, open: false })}>
            {modal.child}
          </Modal>

          <Modal
            title="AI Decision Support"
            opened={aiModal.open}
            size="xl"
            onClose={() =>
              setAiModal({ open: false, loading: false, result: null, error: null })
            }>
            {aiModal.loading && (
              <div className="d-flex flex-column align-items-center gap-3 py-5">
                <div className="ai-confidence-ring">
                  <Loader size="xl" />
                </div>
                <span className="text-muted fw-semibold">Analyzing visit against clinical history...</span>
              </div>
            )}
            {aiModal.error && (
              <div className="text-center py-4">
                <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
                <p className="text-danger mb-0 fw-semibold">{aiModal.error}</p>
              </div>
            )}
            {aiModal.result && !aiModal.loading && (
              <div className="d-flex flex-column gap-4">
                {/* Traffic Light Banner */}
                <div className="text-center">
                  <div className="ai-confidence-ring">
                    <div className={`ai-traffic-circle ${aiModal.result.class.toLowerCase()}`}>
                      {aiModal.result.class}
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="fw-bold" style={{
                      fontSize: 20,
                      color: aiModal.result.class === "Green" ? "#16a34a"
                        : aiModal.result.class === "Yellow" ? "#a16207"
                          : "#dc2626"
                    }}>
                      {aiModal.result.class === "Green"
                        ? "Low Risk"
                        : aiModal.result.class === "Yellow"
                          ? "Needs Attention"
                          : "Action Required"}
                    </span>
                  </div>
                  {aiModal.result.confidence != null && (
                    <div className="mt-2">
                      <span className="text-muted small">
                        AI Confidence: <strong>{aiModal.result.confidence}%</strong>
                      </span>
                    </div>
                  )}
                </div>

                {/* Stacked Cards */}
                <div className="d-flex flex-column gap-3">
                  {/* Assessment */}
                  <div className="rounded-4 p-3" style={{
                    background: aiModal.result.class === "Green"
                      ? "#f0fdf4"
                      : aiModal.result.class === "Yellow"
                        ? "#fefce8"
                        : "#fef2f2"
                  }}>
                    <span className="small fw-semibold text-uppercase mb-2 d-block" style={{
                      color: aiModal.result.class === "Green" ? "#16a34a"
                        : aiModal.result.class === "Yellow" ? "#a16207"
                          : "#dc2626"
                    }}>
                      Assessment
                    </span>
                    {aiModal.result.reason && (
                      <p className="mb-0" style={{ whiteSpace: "pre-line", lineHeight: 1.6 }}>
                        {aiModal.result.reason}
                      </p>
                    )}
                  </div>

                  {/* Clinical Context */}
                  <div className="rounded-4 p-3" style={{
                    background: aiModal.result.class === "Green"
                      ? "#f0fdf4"
                      : aiModal.result.class === "Yellow"
                        ? "#fefce8"
                        : "#fef2f2"
                  }}>
                    <span className="small fw-semibold text-uppercase mb-2 d-block" style={{
                      color: aiModal.result.class === "Green" ? "#16a34a"
                        : aiModal.result.class === "Yellow" ? "#a16207"
                          : "#dc2626"
                    }}>
                      Clinical Context
                    </span>
                    {aiModal.result.explanation ? (
                      <p className="mb-0" style={{ whiteSpace: "pre-line", lineHeight: 1.6 }}>
                        {aiModal.result.explanation}
                      </p>
                    ) : aiModal.result.reason ? (
                      <p className="mb-0" style={{ whiteSpace: "pre-line", lineHeight: 1.6 }}>
                        {aiModal.result.reason}
                      </p>
                    ) : (
                      <p className="text-muted mb-0 small">No additional context available.</p>
                    )}
                  </div>

                  {/* Recommended Actions */}
                  <div className="rounded-4 p-3" style={{ background: "#f0fdf4" }}>
                    <span className="small fw-semibold text-uppercase mb-2 d-block" style={{ color: "#16a34a" }}>
                      Recommended Actions
                    </span>
                    {aiModal.result.recommendation ? (
                      recommendationItems.length > 1 ? (
                        <ol className="mb-0 ps-3" style={{ lineHeight: 1.7 }}>
                          {recommendationItems.map((item, idx) => (
                            <li key={idx} className={idx < recommendationItems.length - 1 ? "mb-2" : ""}>
                              <span style={{ whiteSpace: "pre-line" }}>
                                {item.replace(/^\d+\.\s*/, "")}
                              </span>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="mb-0" style={{ whiteSpace: "pre-line", lineHeight: 1.6 }}>
                          {aiModal.result.recommendation}
                        </p>
                      )
                    ) : (
                      <p className="text-muted mb-0 small">No specific recommendations.</p>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="border-top pt-3 d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    AI Decision Support Logged. Final clinical responsibility remains with attending physician.
                  </small>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="app-btn btn-primary"
                      onClick={() =>
                        setAiModal({ open: false, loading: false, result: null, error: null })
                      }>
                      Acknowledge &amp; Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Modal>

          {/* PDF Viewer Modal */}
          <Modal
            title={`PDF Viewer - ${pdfModal.fileName}`}
            size="90%"
            opened={pdfModal.open}
            onClose={() => setPdfModal({ ...pdfModal, open: false })}
            styles={{
              body: {
                height: "80vh",
                overflow: "hidden",
              },
            }}>
            <div style={{ height: "100%", overflow: "auto" }}>
              <Document
                file={pdfModal.url}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={(error) => {
                  console.error("Error loading PDF:", error);
                  message.error("Failed to load PDF file");
                }}
                loading={<div className="text-center p-4">Loading PDF...</div>}
                error={
                  <div className="text-center p-4 text-danger">
                    Failed to load PDF
                  </div>
                }>
                {numPages &&
                  Array.from(new Array(numPages), (el, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={Math.min(window.innerWidth * 0.8, 800)}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      className="mb-3"
                    />
                  ))}
              </Document>
            </div>
          </Modal>
        </div>
      )}
    </>
  );
};

export default ConsultationDetail;
